/**
 * Keycloak PKCE singleton.
 *
 * We keep a single module-level Keycloak instance so the auth callbacks,
 * the token refresh timer, and the React provider all see the same state.
 * `keycloak-js` maintains its own refresh timer once we call `init()` with
 * `onTokenExpired`, so we just need to re-publish the new access token
 * into the context on every refresh.
 */

import Keycloak, { type KeycloakInitOptions } from "keycloak-js";

import { config } from "@/config";

let instance: Keycloak | null = null;

export function getKeycloak(): Keycloak {
  if (instance === null) {
    instance = new Keycloak({
      url: config.keycloakBaseUrl,
      realm: config.keycloakRealm,
      clientId: config.keycloakClientId,
    });
  }
  return instance;
}

export const DEFAULT_INIT: KeycloakInitOptions = {
  onLoad: "check-sso",
  pkceMethod: "S256",
  checkLoginIframe: false,
  // Keep the URL tidy after the auth redirect; react-router is the source
  // of truth for the visible path.
  silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
};

/**
 * Extract fields we care about from the parsed access token, without
 * re-verifying the signature. The API server verifies the signature on
 * every call; the client just uses these for UI hinting.
 */
export type TokenProfile = {
  username: string | null;
  email: string | null;
  tenantId: string | null;
  roles: string[];
};

export function profileFromToken(kc: Keycloak): TokenProfile {
  const parsed = (kc.tokenParsed ?? {}) as Record<string, unknown>;
  const realmRoles = (parsed["realm_access"] as { roles?: unknown } | undefined)?.roles;
  const roles = Array.isArray(realmRoles)
    ? realmRoles.filter((r): r is string => typeof r === "string")
    : [];
  return {
    username: stringOrNull(parsed["preferred_username"]),
    email: stringOrNull(parsed["email"]),
    tenantId: stringOrNull(parsed["tenant_id"]),
    roles,
  };
}

function stringOrNull(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}
